import { memo } from "react";
import { Alert, Button, Divider, Form, Input, Progress, Select, Space, Typography } from "antd";
import { CheckCircleOutlined, EyeInvisibleOutlined, EyeTwoTone, LockOutlined } from "@ant-design/icons";

import { PASSWORD_STRENGTH_COLORS, PASSWORD_STRENGTH_CRITERIA, PASSWORD_STRENGTH_LABELS } from "../profile.constants";

const { Title, Text } = Typography;



function PasswordStrengthMeter({ pwdStrength }) {
  if (pwdStrength <= 0) return null;

  return (
    <div style={{ marginTop: -16, marginBottom: 20 }}>
      <Progress
        percent={(pwdStrength / 5) * 100}
        showInfo={false}
        size="small"
        strokeColor={PASSWORD_STRENGTH_COLORS[pwdStrength]}
        style={{ marginBottom: 4 }}
      />
      <Text style={{ fontSize: 12, color: PASSWORD_STRENGTH_COLORS[pwdStrength], fontWeight: 600 }}>
        {PASSWORD_STRENGTH_LABELS[pwdStrength]}
      </Text>
      <Space size={16} style={{ marginLeft: 12 }}>
        {PASSWORD_STRENGTH_CRITERIA.map((criterion) => {
          const ok = pwdStrength >= criterion.minScore;
          return (
            <Space key={criterion.label} size={4}>
              <CheckCircleOutlined style={{ color: ok ? "#52c41a" : "#d9d9d9", fontSize: 11 }} />
              <Text style={{ fontSize: 11, color: ok ? "#595959" : "#bfbfbf" }}>{criterion.label}</Text>
            </Space>
          );
        })}
      </Space>
    </div>
  );
}

function ChangePasswordTabComponent({
  form,
  changing,
  pwdStrength,
  securityQuestions,
  securityQuestionsLoading,
  onPasswordChange,
  onSubmit,
}) {
  const securityQuestionOptions = securityQuestions.map((item) => ({
    value: String(item.quesId),
    label: item.question,
  }));

  return (
    <div style={{ maxWidth: 480 }}>
      <Title level={5} style={{ margin: "0 0 4px" }}>
        Update Password
      </Title>
      <Text style={{ color: "#8c8c8c", fontSize: 13, display: "block", marginBottom: 24 }}>
        Choose a strong password to keep your account secure
      </Text>

      <Alert
        type="info"
        showIcon
        message="Your password must be at least 8 characters and include an uppercase letter and a number."
        style={{ marginBottom: 24, borderRadius: 8 }}
      />

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label={<Text style={{ fontWeight: 600 }}>Current Password</Text>}
          name="currentPassword"
          rules={[{ required: true, message: "Enter your current password" }]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
            size="large"
            placeholder="Your current password"
            style={{ borderRadius: 10 }}
            iconRender={(v) => (v ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Divider />

        <Form.Item
          label={<Text style={{ fontWeight: 600 }}>New Password</Text>}
          name="newPassword"
          rules={[
            { required: true, message: "New password is required" },
            { min: 8, message: "At least 8 characters" },
            {
              pattern: /^(?=.*[A-Z])(?=.*\d)/,
              message: "Must contain an uppercase letter and a number",
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
            size="large"
            placeholder="Create a new password"
            style={{ borderRadius: 10 }}
            iconRender={(v) => (v ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            onChange={(e) => onPasswordChange(e.target.value)}
          />
        </Form.Item>

        <PasswordStrengthMeter pwdStrength={pwdStrength} />

        <Form.Item
          label={<Text style={{ fontWeight: 600 }}>Confirm New Password</Text>}
          name="confirmPassword"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Please confirm your new password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
            size="large"
            placeholder="Repeat new password"
            style={{ borderRadius: 10 }}
            iconRender={(v) => (v ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Divider />

        <Form.Item
          label={<Text style={{ fontWeight: 600 }}>Security Question</Text>}
          name="questionId"
          rules={[{ required: true, message: "Security question is required" }]}
        >
          <Select
            size="large"
            placeholder="Select security question"
            loading={securityQuestionsLoading}
            options={securityQuestionOptions}
          />
        </Form.Item>

        <Form.Item
          label={<Text style={{ fontWeight: 600 }}>Security Answer</Text>}
          name="securityAnswer"
          rules={[
            { required: true, message: "Security answer is required" },
            { whitespace: true, message: "Security answer cannot be empty" },
          ]}
        >
          <Input size="large" placeholder="Enter security answer" autoComplete="off" />
        </Form.Item>

        <Button
          type="primary"
          size="large"
          loading={changing}
          onClick={onSubmit}
          style={{ borderRadius: 10, height: 48, fontWeight: 600 }}
          block
        >
          Update Password
        </Button>
      </Form>
    </div>
  );
}

export const ChangePasswordTab = memo(ChangePasswordTabComponent);
